import { createRootRoute, HeadContent, Outlet, Scripts } from "@tanstack/react-router";
import type { ReactNode } from "react";

// oxlint-disable-next-line import/no-unassigned-import
import "@/assets/styles.css";
import { AppError } from "@/components/errors/app-error";
import { NotFound } from "@/components/errors/not-found";
import { Providers } from "@/components/providers/providers";
import { searchHead, SITE_NAME, usesPlatformImage } from "@/lib/seo";
import { themeBootstrapScript } from "@/lib/theme";
import { m } from "@/paraglide/messages.js";
import { getLocale, getUrlOrigin } from "@/paraglide/runtime.js";

const themeBootstrapHtml = { __html: themeBootstrapScript };

export const Route = createRootRoute({
  // Single source of truth for document metadata across SSR, CSR and Electron.
  // SSR renders these into <head>; in the browser React 19 hoists them there. A page's own `head`
  // (see `pageMeta` in lib/seo.ts) replaces any of these by name; the canonical address, hreflang
  // and robots come from `searchHead`, once, for whichever page is showing.
  head: ({ matches }) => {
    const search = searchHead(matches);

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: m.app_title() },
        { name: "description", content: m.app_description() },
        { name: "theme-color", content: "#14110e" },
        // Link previews. Crawlers do not resolve relative URLs here, so og:image is absolute -
        // getUrlOrigin() is the public origin under SSR (see server.ts) and the page's in a browser.
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: SITE_NAME },
        { property: "og:title", content: m.app_title() },
        { property: "og:description", content: m.app_description() },
        ...(usesPlatformImage(matches)
          ? [
              { property: "og:image", content: `${getUrlOrigin()}/assets/og-image.png` },
              { property: "og:image:type", content: "image/png" },
              { property: "og:image:width", content: "1200" },
              { property: "og:image:height", content: "630" },
              { property: "og:image:alt", content: m.app_description() },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        ...search.meta,
      ],
      // Generated by scripts/brand-assets.mjs. The ICO covers browsers without SVG favicons; the
      // SVG one follows the tab strip's colour scheme.
      links: [
        { rel: "icon", href: "/favicon.ico", sizes: "32x32" },
        { rel: "icon", type: "image/svg+xml", href: "/assets/icon.svg" },
        { rel: "apple-touch-icon", sizes: "180x180", href: "/assets/apple-touch-icon.png" },
        { rel: "manifest", href: "/site.webmanifest" },
        ...search.links,
      ],
    };
  },
  notFoundComponent: NotFound,
  errorComponent: AppError,
  component: Root,
});

/**
 * Under SSR React emits the entire document and hydrates against it, so the
 * wrapper has to come from here. The CSR and Electron builds take it from
 * index.html and mount into #root, so it is compiled out of those bundles.
 */
function DocumentShell({ children }: { children: ReactNode }) {
  if (!__SSR_SHELL__) {
    return children;
  }

  return (
    // The theme script below mutates the class before React hydrates, which is
    // the point - suppressing lets that intentional difference through.
    <html lang={getLocale()} suppressHydrationWarning>
      <head>
        <HeadContent />
        {/* eslint-disable-next-line react/no-danger */}
        <script dangerouslySetInnerHTML={themeBootstrapHtml} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function Root() {
  return (
    <DocumentShell>
      <Providers>
        <Outlet />
      </Providers>
      {!__SSR_SHELL__ && <HeadContent />}
    </DocumentShell>
  );
}
